import { Loader } from '@storybook';
import { Elements } from '@stripe/react-stripe-js';
import { ReactNode, useCallback, useMemo } from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import {
	AddPaymentDetails,
	ChooseIdentity,
	ErrorPage,
	SubscribeProcessing,
	SuccessPage,
} from './components';
import { IdentityHeaderData, IndetityFlowNavigate } from './constants';
import { useChooseIdentity, useDefaultCard, useUserIdentity } from './hooks';
import {
	BillingPaymentAddress,
	ChooseUserIdentity,
	IdentityFlowError,
	IdentityFlowRetry,
} from './store';
import {
	PaymentChoose,
	WireScreen,
	AddPaymentCard,
	CardView,
	paymentCardTypes,
	ACH,
	ChooseAchDefault,
	BillingInfoForm,
	PaySubscriptionAmount,
	ExpireCardAlert,
} from 'views/settings';
import { PaymentCardMethodsState } from 'global-stores';
import { KycWebComponents } from 'components';
import classNames from 'classnames';
import { stripePromise } from 'main';
import { Modal } from 'views/design-system/components';
import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import './user-identity-flow.scss';

const HEADED_STEPS = new Set([
	IndetityFlowNavigate.ChooseIdentity,
	IndetityFlowNavigate.ChooseMethod,
	IndetityFlowNavigate.SubscriptionReview,
	IndetityFlowNavigate.PaymentCard,
	IndetityFlowNavigate.Wire,
	IndetityFlowNavigate.CardView,
	IndetityFlowNavigate.WirePage,
	IndetityFlowNavigate.AchView,
	IndetityFlowNavigate.DefaultAchPage,
	IndetityFlowNavigate.BillingForm,
]);

type TUserIdentityFlow = {
	handleCloseModal: () => void;
	isOpen: boolean;
};

/**
 * THE SIGNUP / SUBSCRIBE JOURNEY, ON THE v2 DESIGN SYSTEM.
 *
 * This used to be the storybook `Modal`, portaled to <body> OUTSIDE every
 * `.sv2-scope`. Two things followed from that, both visible in the same
 * screenshot: the dialog painted the legacy light palette over a dark page,
 * and the Stripe card fields — which read the THEME from `<html>` and hand
 * their colours to a cross-origin iframe as literals — rendered near-white
 * text on that white field. Typing into "Card number" showed nothing.
 *
 * The design-system `Modal` carries `sv2-scope sv2-modal` on its portal root,
 * so the tokens, the dialog primitives and the theme all resolve inside it;
 * `sv2-embed-scope` remaps the panel's own `--color-*` names for the legacy
 * screens this flow still mounts whole (the card form, the card list, Plaid,
 * the address form). The Modal claims the sv2 theme for as long as it is open,
 * so the journey is dark by default even when it opens over a legacy route —
 * and the claim is released on close, which a claim made HERE could not do:
 * this component is mounted permanently by the route shell.
 */
export const UserIdentityFlow = ({
	isOpen,
	handleCloseModal,
}: TUserIdentityFlow) => {
	const resetActiveIdx = useResetRecoilState(ChooseUserIdentity);
	const resetBillingAddress = useResetRecoilState(BillingPaymentAddress);
	const resetFlowError = useResetRecoilState(IdentityFlowError);
	const resetFlowRetry = useResetRecoilState(IdentityFlowRetry);

	// Hooks
	const { handleClickChoose } = useChooseIdentity();
	const {
		handleNextUserIdentity,
		handleBackUserIdentity,
		navigate,
		setNavigate,
		mapToNavigationNextLabel,
		isLoading,
		isLoadingPlaid,
		handleChooseNavigate,
		handleSumbitKycDetails,
		onSubmitBusinessIdentity,
		retryUpgrade,
		activeIdx,
	} = useUserIdentity({ handleCloseModal });

	const { isDefaultExist } = useDefaultCard();
	const paymentCardMethodsState = useRecoilValue(PaymentCardMethodsState);
	const billingAddress = useRecoilValue(BillingPaymentAddress);

	const { banks = [], cards = [] } = useMemo(
		() => paymentCardMethodsState ?? {},
		[paymentCardMethodsState]
	);

	const closeModal = useCallback(async () => {
		handleCloseModal();
		setTimeout(() => {
			resetActiveIdx();
			resetBillingAddress();
			resetFlowError();
			resetFlowRetry();
		}, 500);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const mapToNavigationBody: Record<string, ReactNode> = useMemo(
		() => ({
			[IndetityFlowNavigate.ChooseIdentity]: (
				<ChooseIdentity handleClickChoose={handleClickChoose} />
			),
			[IndetityFlowNavigate.PersonalIdentity]:
				activeIdx === 'entity' ? (
					<KycWebComponents
						type="kyb"
						handleSuccess={onSubmitBusinessIdentity}
						businessType={activeIdx}
					/>
				) : (
					<KycWebComponents
						type="kyc"
						handleSuccess={handleSumbitKycDetails}
						businessType={activeIdx}
					/>
				),
			[IndetityFlowNavigate.Payment]: (
				<Elements stripe={stripePromise}>
					<AddPaymentDetails setNavigate={setNavigate} />
				</Elements>
			),
			[IndetityFlowNavigate.Processing]: <SubscribeProcessing />,
			[IndetityFlowNavigate.Success]: <SuccessPage handleClose={closeModal} />,
			[IndetityFlowNavigate.Error]: (
				<ErrorPage handleClose={closeModal} onRetryUpgrade={retryUpgrade} />
			),
			[IndetityFlowNavigate.ChooseMethod]: <PaymentChoose />,
			[IndetityFlowNavigate.Wire]: <WireScreen />,
			[IndetityFlowNavigate.PaymentCard]: <AddPaymentCard from="SIGNUP" />,
			[IndetityFlowNavigate.CardView]: <CardView fromIdentity />,
			[IndetityFlowNavigate.SubscriptionReview]: <PaySubscriptionAmount />,
			[IndetityFlowNavigate.ExpireCard]: <ExpireCardAlert />,
			[IndetityFlowNavigate.AchView]: <ACH isSignup />,
			[IndetityFlowNavigate.WirePage]: <WireScreen isViewPage />,
			[IndetityFlowNavigate.DefaultAchPage]: <ChooseAchDefault />,
			[IndetityFlowNavigate.BillingForm]: <BillingInfoForm />,
		}),

		[
			activeIdx,
			closeModal,
			handleClickChoose,
			handleSumbitKycDetails,
			onSubmitBusinessIdentity,
			retryUpgrade,
			setNavigate,
		]
	);

	const renderBody = useMemo(
		() => mapToNavigationBody[navigate],
		[mapToNavigationBody, navigate]
	);

	/* The dialog head carries the step's title and one line of context. The
	   processing, success and error screens and the full-viewport KYC/KYB embed
	   speak for themselves and get no head — the same set the old header map
	   left out. */
	const head = HEADED_STEPS.has(navigate) ? IdentityHeaderData[navigate] : undefined;

	const hasBillingInfo = useMemo(() => {
		const allValuesPresent =
			Object.entries(billingAddress ?? {})
				.filter(([key]) => key !== 'address2')
				.every(([, value]) => value !== '') &&
			(billingAddress?.zip?.length ?? 0) > 3;
		return !allValuesPresent;
	}, [billingAddress]);

	const disableNextBtn = useMemo(() => {
		switch (navigate) {
			case IndetityFlowNavigate.BillingForm:
				return hasBillingInfo;
			default:
				return false;
		}
	}, [hasBillingInfo, navigate]);

	const shouldHideBtn = useMemo(() => {
		if (banks.length > 0 || cards.length > 0) {
			return /Success|BusinessIdentity|PersonalIdentity|Processing|Payment|ExpireCard|Error/.test(
				navigate
			);
		} else {
			return /Success|Processing|BusinessIdentity|PersonalIdentity|Payment|Error|AchView|ExpireCard|CardView/.test(
				navigate
			);
		}
	}, [banks?.length, cards?.length, navigate]);

	const isHideBackBtn = useMemo(() => {
		if (isDefaultExist) {
			return /Wire|AchDefaultAccount|CardView|AchView|WireView/.test(navigate);
		} else {
			return /AchDefaultAccount/.test(navigate);
		}
	}, [navigate, isDefaultExist]);

	const isShowSidebar = useMemo(
		() => /CardView|AchView|WireView/.test(navigate),
		[navigate]
	);

	const renderSideNavigation = useMemo(
		() => (
			<nav className="idflow__rail" aria-label="Payment method">
				{paymentCardTypes.map(type => (
					<button
						key={type.key}
						type="button"
						onClick={handleChooseNavigate(type.key)}
						className={classNames('idflow__rail-item', {
							'is-active': type.key === navigate,
						})}
						disabled={type.key === 'WireView'}
					>
						<i className={type.icon} aria-hidden="true" />
						{type.label}
					</button>
				))}
			</nav>
		),
		[handleChooseNavigate, navigate]
	);

	const isShowCancelIcon = useMemo(
		() =>
			[
				IndetityFlowNavigate.BillingForm,
				IndetityFlowNavigate.CardView,
				IndetityFlowNavigate.AchView,
				IndetityFlowNavigate.WirePage,
				IndetityFlowNavigate.Success,
				IndetityFlowNavigate.PersonalIdentity,
				IndetityFlowNavigate.BusinessIdentity,
				/* A terminal failure MUST be escapable. Error shows no footer
				   (shouldHideBtn matches it) and ErrorPage ignores the
				   handleClose it is handed, so without the X this step had no
				   exit at all: Escape is off, the backdrop is inert, and Retry
				   can keep failing. Reloading the page was the only way out. */
				IndetityFlowNavigate.Error,
			].includes(navigate),
		[navigate]
	);

	const isKYBKYCPage = useMemo(
		() => /BusinessIdentity|PersonalIdentity/.test(navigate),
		[navigate]
	);

	const footer =
		!shouldHideBtn && (
			<>
				{!isHideBackBtn && (
					<button
						type="button"
						className="btn"
						onClick={handleBackUserIdentity}
						disabled={isLoading}
					>
						Back
					</button>
				)}
				<button
					type="button"
					className="btn pri"
					onClick={handleNextUserIdentity}
					disabled={disableNextBtn || isLoadingPlaid || isLoading}
					aria-busy={isLoading || isLoadingPlaid || undefined}
				>
					{isLoading || isLoadingPlaid ? (
						<Loader type="loader" dimension={14} />
					) : null}
					{mapToNavigationNextLabel}
				</button>
			</>
		);

	return (
		<Modal
			open={isOpen}
			title={head?.head ?? ''}
			sub={head?.desc}
			onClose={closeModal}
			dismissable={isShowCancelIcon}
			/* The journey is multi-step and holds typed input; only the X ends it.
			   The storybook modal it replaced set the same two flags off. */
			lightDismiss={false}
			ariaLabel={head?.head ?? 'Account setup'}
			size="wide"
			/* `idflow--full` is the only modifier with rules behind it; the rail
			   and per-step classes were emitted but styled by nothing. */
			className={classNames('idflow', 'sv2-embed-scope', {
				'idflow--full': isKYBKYCPage,
			})}
			footer={footer || undefined}
		>
			<div className="idflow__body">
				{isShowSidebar && renderSideNavigation}
				<div className="idflow__stage">
					{isLoading ? (
						<div className="idflow__loader">
							<Loader />
						</div>
					) : (
						renderBody
					)}
				</div>
			</div>
		</Modal>
	);
};
